"use client";

/**
 * ReflectScreen — Decide › Reflect tab.
 *
 * 2026-05-06 redesign: replaced the legacy stack of `SynthesisBanner`,
 * `MiniLifeWheel + per-area summary`, "What's drifting?" goal grid, and
 * `SuggestionCard` with the design from the Reflect handoff (tin-2.zip).
 *
 * New top-to-bottom order:
 *   1. Page header (eyebrow + title + lede)
 *   2. Balance Hero (summary stats + per-area rows + per-goal expansion)
 *   3. AI Suggestion ("What's worth considering")
 *   4. Rhythms grid (Drift / Steady)
 *   5. Still Echoing (tone / long goal / window / check-in chips)
 *
 * The component still owns the same data loading the old ReflectScreen
 * did — projection + goals + drift summary + current recommendation +
 * settings. Each block degrades gracefully when its API source is empty
 * or unavailable; we never render synthetic placeholders.
 */

import { useEffect, useMemo, useState } from "react";
import {
  acceptRecommendationAsTask,
  createScheduleBlock,
  getCurrentRecommendation,
  getGoalDriftSummary,
  getLifeAreas,
  listGoals,
  type CurrentRecommendation,
  type GoalDriftSummary,
  type GoalItem,
  type LifeAreaItem,
} from "@/lib/api";
import { authFetch } from "@/lib/api/base";
import { getSettings, type Settings } from "@/lib/settings-api";
import { useReflectProjection } from "@/hooks/useReflectProjection";
import { buildGoalFocusTarget } from "@/lib/goalFocusHandoff";
import { inferDateFromLabel } from "@/lib/inferDateFromLabel";
import { pickRevealKind } from "@/lib/reveal/route_reveal";
import { useGoalFocusContext, useDataRefresh } from "@/lib/store";
import { useChatRevealStore } from "@/lib/stores/chatReveal";
import MemorySection from "./MemorySection";
import ReflectAiSuggestion, {
  type ReflectAiSuggestionItem,
} from "./ReflectAiSuggestion";
import ReflectBalanceHero from "./ReflectBalanceHero";
import ReflectRhythmsGrid from "./ReflectRhythmsGrid";
import ReflectStillEchoing, {
  type ReflectStillEchoingChip,
} from "./ReflectStillEchoing";
import styles from "./reflect-page.module.css";

interface Props {
  onNavigate: (tab: string, initialMessage?: string) => void;
}

const TONE_LABELS: Record<string, string> = {
  warm_coach: "warm coach",
  direct_mentor: "direct mentor",
  gentle_friend: "gentle friend",
  hype_partner: "hype partner",
};

// `formatTimeWindow` was used to surface quiet hours as a "Window" chip,
// but that label was misleading (a "22:00–07:45" window read like "you're
// available 22:00 to 07:45"). Replaced by working-hours-only.

// Imperative verbs we recognise as the start of a concrete task. Kept
// short on purpose — false positives (e.g. picking a wrong sentence)
// are worse than falling back to open_goal.
const IMPERATIVE_VERBS = [
  "set aside",
  "schedule",
  "block",
  "watch",
  "read",
  "open",
  "practice",
  "write",
  "draft",
  "send",
  "email",
  "call",
  "review",
  "plan",
  "complete",
  "finish",
  "start",
  "begin",
  "do",
  "try",
  "spend",
  "add",
  "create",
  "list",
  "outline",
  "sketch",
  "explore",
];

const IMPERATIVE_RE = new RegExp(
  `^\\s*(?:${IMPERATIVE_VERBS.join("|")})\\b`,
  "i",
);

const FILLER_PATTERNS = [
  /^to (kickstart|begin|start|embark|jump|continue|further|build)/i,
  /^this will (help|allow|enable)/i,
  /^enjoy /i,
  /^have fun /i,
];

function isFiller(sentence: string): boolean {
  return FILLER_PATTERNS.some((re) => re.test(sentence.trim()));
}

function isConcreteSentence(sentence: string): boolean {
  const s = sentence.trim();
  if (s.length < 4 || s.length > 100) return false;
  if (isFiller(s)) return false;
  return IMPERATIVE_RE.test(s) || /\b\d+\s*(min|minutes|hr|hour|hours)\b/i.test(s);
}

/** Concrete task-title classifier. Three-stage pick:
 *
 *   1. roadmap_step (≥ 4 chars)              → use it
 *   2. content as one chunk if clean+short    → use it
 *   3. split content into sentences, pick the first one that starts
 *      with an imperative verb OR mentions a duration ("30 minutes")
 *
 *   Otherwise return null → host routes user to "Open goal to define
 *   step" so they pick a real next step themselves rather than getting
 *   a placeholder task. */
function deriveConcreteTaskTitle(rec: CurrentRecommendation): string | null {
  const step = (rec.roadmap_step ?? "").trim();
  if (step.length >= 4) return truncate(step, 100);

  const content = rec.content.trim();

  // Whole-content shortcut.
  const cleanWhole =
    content.length >= 4 &&
    content.length <= 80 &&
    content.split(/[,.;]/).length <= 2 &&
    !content.endsWith("…") &&
    !isFiller(content);
  if (cleanWhole) return content;

  // Sentence extraction. Recommendation engine often emits one
  // marketing intro sentence followed by the actual concrete action.
  // Split on . ! ? while keeping multi-word phrases intact.
  const sentences = content
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
  for (const sentence of sentences) {
    if (isConcreteSentence(sentence)) {
      // Drop trailing punctuation; cap length.
      return truncate(sentence.replace(/[.!?]+\s*$/, ""), 100);
    }
  }

  return null;
}

/** Render a resolved Date as a human button/banner label: "Saturday,
 *  May 30". Locale-aware — falls back to ISO date if Intl is broken. */
function formatScheduledFor(d: Date): string {
  try {
    return d.toLocaleDateString(undefined, {
      weekday: "long",
      month: "short",
      day: "numeric",
    });
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

/** What "Try this" should DO, based on the rec's shape. The button label
 *  follows the action so the user knows what's about to happen — not the
 *  generic "Try this" that hides the side effect.
 *
 *  `scheduledStart` is the already-resolved Date the schedule_block
 *  action will land on (computed via resolveScheduleStart). It's passed
 *  in so the button + accepted banner can show the REAL date the block
 *  will sit on, not the LLM's literal label ("this weekend" was getting
 *  rendered alongside a Thursday block — see dogfood B3). */
function pickAction(
  rec: CurrentRecommendation,
  scheduledStart: Date | null,
): {
  action:
    | "schedule_block"
    | "add_task"
    | "save_goal"
    | "open_goal"
    | "noted";
  action_label: string;
  accepted_label: string;
  task_title: string;
} {
  // Anchor to an existing goal: prefer the most committal action the
  // rec supports.
  //   1. inferred_schedule + duration  → "Schedule it" (block on calendar)
  //   2. concrete sentence available    → "Add as task"
  //   3. else                           → "Open goal" (user picks step)
  if (rec.goal_id) {
    const sched = rec.inferred_schedule ?? null;
    if (sched && sched.duration_minutes) {
      const taskTitle = deriveConcreteTaskTitle(rec) ?? rec.content.trim();
      // Honest accepted-banner copy: render the real resolved date
      // (weekday + month + day) rather than the LLM label. If the
      // label says "weekend" but the resolved date is a weekday, we
      // would otherwise be lying to the user — surface a dev warning
      // and always trust the resolved date.
      const labelSaysWeekend =
        !!sched.label && /\bweekend\b/i.test(sched.label);
      const resolvedDow = scheduledStart ? scheduledStart.getDay() : null;
      const resolvedIsWeekend = resolvedDow === 0 || resolvedDow === 6;
      if (
        process.env.NODE_ENV !== "production" &&
        labelSaysWeekend &&
        resolvedDow !== null &&
        !resolvedIsWeekend
      ) {
        // eslint-disable-next-line no-console
        console.warn(
          `[reflect] sched.label says "weekend" but resolved start is a weekday (${resolvedDow}); rendering real date.`,
        );
      }
      const buttonLabel = scheduledStart
        ? `Schedule for ${formatScheduledFor(scheduledStart)}`
        : sched.label
        ? `Schedule for ${sched.label}`
        : `Schedule ${sched.duration_minutes}m`;
      const acceptedLabel = scheduledStart
        ? `✓ Scheduled "${truncate(taskTitle, 50)}" for ${formatScheduledFor(scheduledStart)}.`
        : sched.label
        ? `✓ Scheduled "${truncate(taskTitle, 50)}" for ${sched.label}.`
        : `✓ Scheduled "${truncate(taskTitle, 50)}" on your calendar.`;
      return {
        action: "schedule_block",
        action_label: buttonLabel,
        accepted_label: acceptedLabel,
        task_title: taskTitle,
      };
    }
    const taskTitle = deriveConcreteTaskTitle(rec);
    if (taskTitle) {
      return {
        action: "add_task",
        action_label: "Add as task",
        accepted_label: `✓ Added "${truncate(taskTitle, 60)}" to your tasks.`,
        task_title: taskTitle,
      };
    }
    return {
      action: "open_goal",
      action_label: "Open goal to define step",
      accepted_label:
        "✓ Take a look at the goal — pick the next step that fits.",
      task_title: "",
    };
  }
  // No goal anchor + the engine flagged it as a goal-shaped suggestion →
  // create the goal lightly via the existing /accept endpoint (which
  // creates a Goal under category="personal"). This is the only path
  // where a brand-new goal is created.
  if (rec.type === "goal") {
    return {
      action: "save_goal",
      action_label: "Save as goal",
      accepted_label: `✓ Saved "${truncate(rec.content, 60)}" as a new goal.`,
      task_title: rec.content,
    };
  }
  // Anything else (strategy, library, energy, etc.): pure status flip.
  return {
    action: "noted",
    action_label: "Mark as noted",
    accepted_label: "✓ Noted. We'll check in next week.",
    task_title: rec.content,
  };
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  // Cut at the last word boundary before `max` so we don't end mid-word.
  const slice = s.slice(0, max - 1);
  const lastSpace = slice.lastIndexOf(" ");
  return `${lastSpace > max * 0.6 ? slice.slice(0, lastSpace) : slice}…`;
}

/** Pick a concrete start `Date` from an inferred schedule + user
 *  settings.
 *
 *  Resolution order (per dogfood-output/reflect-schedule-button-2026-05-28
 *  — the "Schedule for this weekend lands on Thursday" bug):
 *    1. explicit `target_date` from the backend → use it
 *    2. else parse `sched.label` ("this weekend", "monday", "tonight"…)
 *       via `inferDateFromLabel` — this is what fixes the bug. The
 *       previous code silently fell back to tomorrow when target_date
 *       was missing, so a Wednesday user clicking "Schedule for this
 *       weekend" got a Thursday block.
 *    3. else tomorrow (legacy fallback so previously-unlabelled recs
 *       still behave the same).
 *
 *  Past-time guard: if step 1/2/3 lands on today AND the preferred hour
 *  has already passed, the block is pushed one hour into the future so
 *  it doesn't appear in the past (mirrors dogfood R3 bug #1 fix).
 *
 *  The returned Date is in the LOCAL timezone — caller serialises via
 *  toISOString. */
function resolveScheduleStart(
  sched: {
    duration_minutes: number;
    target_date?: string | null;
    preferred_start_hour?: number | null;
    label?: string | null;
  },
  settings: Settings | null,
  now: Date = new Date(),
): Date {
  const fallbackHour =
    sched.preferred_start_hour ?? settings?.day_start_hour ?? 9;

  let day: Date | null = null;

  if (sched.target_date) {
    const parsed = new Date(`${sched.target_date}T00:00:00`);
    if (!isNaN(parsed.getTime())) {
      day = parsed;
    }
  }

  if (day === null) {
    day = inferDateFromLabel(sched.label ?? null, now);
  }

  if (day === null) {
    // Final legacy fallback — preserved so callers without a label or
    // target_date keep their old behaviour.
    day = new Date(now);
    day.setDate(day.getDate() + 1);
  }

  day.setHours(fallbackHour, 0, 0, 0);

  // Past-time guard: if we ended up on today and the slot's already in
  // the past, push it forward one hour so we never schedule "9am" at
  // 3pm. Matches the Plan AI Time Box guard added in PR #21.
  const sameCalendarDay =
    day.getFullYear() === now.getFullYear() &&
    day.getMonth() === now.getMonth() &&
    day.getDate() === now.getDate();
  if (sameCalendarDay && day.getTime() <= now.getTime()) {
    const pushed = new Date(now);
    pushed.setHours(now.getHours() + 1, 0, 0, 0);
    return pushed;
  }

  return day;
}

/** Build a human label for an inferred slot when the engine didn't
 *  provide one. Falls back to the duration when no date / hour. */
function humanSlotLabel(
  s: { duration_minutes: number; target_date?: string | null; preferred_start_hour?: number | null },
): string | null {
  const dur = `${s.duration_minutes}m`;
  if (s.target_date) {
    try {
      const d = new Date(`${s.target_date}T00:00:00`);
      const day = d.toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
      if (s.preferred_start_hour != null) {
        const hr = String(s.preferred_start_hour).padStart(2, "0");
        return `${day} ${hr}:00 (${dur})`;
      }
      return `${day} (${dur})`;
    } catch {
      return dur;
    }
  }
  return dur;
}

function buildAiSuggestions(
  rec: CurrentRecommendation | null,
  settings: Settings | null,
): ReflectAiSuggestionItem[] {
  if (!rec || !rec.id || rec.id === "none") return [];
  const cite = {
    area: rec.goal_title ?? null,
    priority: null,
    scheduled: null,
    target: null,
  };
  // Compute the resolved start FIRST so pickAction can render an honest
  // accepted_label and the host can attach reveal_kind to the item.
  // For non-schedule actions scheduledStart stays null and downstream
  // code falls back to legacy goal-reveal.
  const sched = rec.inferred_schedule ?? null;
  const scheduledStart =
    rec.goal_id && sched && sched.duration_minutes
      ? resolveScheduleStart(sched, settings)
      : null;
  const a = pickAction(rec, scheduledStart);
  const revealKind =
    a.action === "schedule_block" ? pickRevealKind(scheduledStart) : "goal";
  return [
    {
      id: rec.id,
      cite,
      headline: rec.content,
      because:
        rec.reason ??
        "This was the strongest signal in this week's reflection — protecting the next move tends to compound.",
      goal_id: rec.goal_id ?? null,
      goal_title: rec.goal_title ?? null,
      action: a.action,
      action_label: a.action_label,
      accepted_label: a.accepted_label,
      // Without this, `handleTry` reads `s.task_title` as undefined,
      // bails on the empty-title guard, and the UI flips to accepted
      // even though no task got created. UAT P15 (2026-05-07).
      task_title: a.task_title,
      // Reveal-time honesty fields (see ReflectAiSuggestion.tsx).
      scheduled_start: scheduledStart,
      reveal_kind: revealKind,
    },
  ];
}

function buildEchoChips(
  settings: Settings | null,
  goals: GoalItem[],
): ReflectStillEchoingChip[] {
  const chips: ReflectStillEchoingChip[] = [];
  if (settings?.tone_preference) {
    const label = TONE_LABELS[settings.tone_preference] ?? settings.tone_preference;
    chips.push({ label: "Tone", value: label });
  }
  // Longest-running active goal — proxy for "long-term commitment".
  const longGoal = goals
    .filter((g) => g.status === "active")
    .sort(
      (a, b) =>
        new Date(a.created_at ?? 0).getTime() -
        new Date(b.created_at ?? 0).getTime(),
    )[0];
  if (longGoal) {
    chips.push({ label: "Long goal", value: longGoal.title });
  }
  if (settings?.day_start_hour != null && settings?.day_end_hour != null) {
    chips.push({
      label: "Working hours",
      value: `${String(settings.day_start_hour).padStart(2, "0")}:00–${String(
        settings.day_end_hour,
      ).padStart(2, "0")}:00`,
    });
  }
  return chips;
}

function ReflectLoading() {
  return (
    <div className="space-y-4">
      <div className="h-44 animate-pulse rounded-3xl border border-border bg-card" />
      <div className="h-40 animate-pulse rounded-3xl border border-border bg-card" />
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="h-44 animate-pulse rounded-2xl border border-border bg-card" />
        <div className="h-44 animate-pulse rounded-2xl border border-border bg-card" />
      </div>
    </div>
  );
}

export default function ReflectScreen({ onNavigate }: Props) {
  const { projection, status, error, refresh } = useReflectProjection();
  const notifyGoalChange = useDataRefresh((s) => s.notifyGoalChange);
  const notifyTaskChange = useDataRefresh((s) => s.notifyTaskChange);
  const notifyScheduleChange = useDataRefresh((s) => s.notifyScheduleChange);
  const setGoalFocusTarget = useGoalFocusContext((s) => s.setTarget);

  const [goals, setGoals] = useState<GoalItem[]>([]);
  const [driftSummary, setDriftSummary] = useState<GoalDriftSummary | null>(null);
  const [recommendation, setRecommendation] =
    useState<CurrentRecommendation | null>(null);
  const [recommendationLoading, setRecommendationLoading] = useState(true);
  const [settings, setSettings] = useState<Settings | null>(null);
  // Direct LifeArea read so the "committed" minutes shown on Reflect's
  // Balance Hero match Balance's BalanceList exactly. Earlier we relied
  // only on projection.allocation.areas.target_minutes_per_week, which
  // can lag the source-of-truth LifeArea.target_minutes_per_week through
  // the projection cache after a Balance edit.
  const [lifeAreas, setLifeAreas] = useState<LifeAreaItem[]>([]);
  // CLCD Addon: when the user accepts a `schedule_block` recommendation,
  // remember the resulting block's id + the kind of reveal we should fire
  // (today vs plan) + the block's local-date string. Cleared on the next
  // handleTry. Without this, the "View in Today/Plan" reveal button can
  // navigate but cannot pulse the newly-created row — Plan's
  // single-day slice would still be showing TODAY when the block lives on
  // FRIDAY. The `date` field (YYYY-MM-DD, local) tells the destination
  // surface which day to switch to before its `data-reveal-id` query
  // can match.
  const [lastScheduledReveal, setLastScheduledReveal] = useState<
    { id: string; kind: "today" | "plan"; date: string | null } | null
  >(null);
  const requestChatReveal = useChatRevealStore((state) => state.request);

  // Load goal context + drift summary + LifeArea source-of-truth.
  // LifeArea is fetched here (not derived from projection) so the
  // committed minutes on Reflect's Balance Hero stay 1:1 with what
  // Balance shows after an edit, even before the projection cache
  // refreshes.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const [goalsResp, drift, lifeResp] = await Promise.all([
          listGoals("active"),
          getGoalDriftSummary().catch(() => null),
          getLifeAreas().catch(() => null),
        ]);
        if (cancelled) return;
        setGoals(goalsResp.goals);
        setDriftSummary(drift);
        setLifeAreas(lifeResp?.areas ?? []);
      } catch {
        if (cancelled) return;
        setGoals([]);
        setDriftSummary(null);
        setLifeAreas([]);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, []);

  // Recommendation: fetch once on mount only. Re-fetching on every
  // projection change re-triggered the LLM-backed `RecommendationEngine`
  // and made Reflect feel sluggish. The user can manually refresh by
  // skipping the current rec (which advances to the next pending one).
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setRecommendationLoading(true);
      try {
        const rec = await getCurrentRecommendation();
        if (!cancelled) {
          setRecommendation(rec.id && rec.id !== "none" ? rec : null);
        }
      } catch {
        if (!cancelled) setRecommendation(null);
      } finally {
        if (!cancelled) setRecommendationLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, []);

  // Settings load once. Failures are non-fatal — chip row hides empty values.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const s = await getSettings();
        if (!cancelled) setSettings(s);
      } catch {
        if (!cancelled) setSettings(null);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, []);

  const aiSuggestions = useMemo(
    () => buildAiSuggestions(recommendation, settings),
    [recommendation, settings],
  );
  const echoChips = useMemo(
    () => buildEchoChips(settings, goals),
    [settings, goals],
  );

  // Try this: actually DO the thing the rec implies, instead of routing
  // through Quick Capture. Action picked by `pickAction()` + surfaced
  // via the button label so the user knows what's about to happen:
  //   - add_task : single atomic POST /api/recommendations/{id}/accept-as-task
  //                that creates the task AND flips the rec to 'accepted'
  //                in one DB transaction. Replaces the previous two-step
  //                flow which could leave a half-state if the user
  //                navigated mid-flight (UAT 2026-05-07, P15).
  //   - open_goal: navigate to the goal so the user can pick the real
  //                next step (used when the rec is too vague to spawn a
  //                useful task — better than dumping a "Next step on X"
  //                placeholder into Today)
  //   - save_goal: POST /api/recommendations/{id}/accept (creates Goal)
  //   - noted    : POST /api/recommendations/{id}/mark-accepted only
  const handleTry = async (s: ReflectAiSuggestionItem): Promise<void> => {
    // The component awaits this. Throw on any early-return so the
    // "✓ Added" banner doesn't fire when nothing got persisted
    // (UAT P15, 2026-05-07). Genuine network failures inside the
    // try-blocks below are still caught and re-thrown.
    if (!recommendation) {
      throw new Error("recommendation_missing");
    }
    try {
      if (s.action === "schedule_block" && s.goal_id) {
        // Time-bound rec → write a real schedule block + mark-accepted.
        // We resolve the start time from inferred_schedule.target_date +
        // preferred_start_hour. Missing target_date → tomorrow. Missing
        // hour → user's day_start_hour from settings (or 9 as fallback).
        const sched = recommendation.inferred_schedule;
        if (sched && sched.duration_minutes) {
          const title = (s.task_title ?? "").trim() || recommendation.content;
          const goal = goals.find((g) => g.id === s.goal_id);
          const start = resolveScheduleStart(sched, settings);
          const end = new Date(
            start.getTime() + sched.duration_minutes * 60_000,
          );
          const createdBlock = await createScheduleBlock({
            kind: "task",
            title,
            description: (recommendation.reason ?? "").trim() || undefined,
            start_time: start.toISOString(),
            end_time: end.toISOString(),
            commitment_level: "planned",
            goal_id: s.goal_id,
            goal_title: goal?.title ?? s.goal_title ?? undefined,
            life_area: goal?.category ?? undefined,
            rationale: (recommendation.reason ?? "").trim() || undefined,
          });
          // Remember which block we just created so the next "View in
          // Today/Plan" click can hand its id + local date to
          // useChatRevealStore. The reveal_kind on `s` was already
          // computed by pickAction from the resolved start date — reuse
          // it so the destination matches the toast text the user just
          // read.
          //
          // The local date (YYYY-MM-DD) is sliced from the resolved
          // `start` Date, not from `createdBlock.start_time`: the
          // server-returned ISO is UTC, and converting it back to local
          // can flip the date across midnight for evening blocks. `start`
          // is already in the user's local timezone (it's the Date object
          // we built from inferred_schedule + day_start_hour), so its
          // toLocaleDateString slice is the day the user actually sees in
          // Plan's date picker.
          const localDateForPlan = `${start.getFullYear()}-${String(
            start.getMonth() + 1,
          ).padStart(2, "0")}-${String(start.getDate()).padStart(2, "0")}`;
          if (s.reveal_kind === "today" || s.reveal_kind === "plan") {
            setLastScheduledReveal({
              id: createdBlock.id,
              kind: s.reveal_kind,
              date: localDateForPlan,
            });
          } else {
            setLastScheduledReveal(null);
          }
          await authFetch(`/api/recommendations/${s.id}/mark-accepted`, {
            method: "POST",
          }).catch(() => undefined);
        }
      } else if (s.action === "add_task" && s.goal_id) {
        // `s.task_title` was decided up-front in `pickAction()` and
        // already passed the concreteness gate. Use rec.reason as the
        // description so the user sees WHY this step was suggested
        // when they open the task — not the marketing-copy headline.
        const title = (s.task_title ?? "").trim();
        if (!title) {
          throw new Error("missing_task_title");
        }
        const description = (recommendation.reason ?? "").trim() || undefined;
        // Atomic write — even if the client navigates away after
        // showing the optimistic confirmation, `keepalive: true` on
        // the underlying fetch lets the request complete server-side,
        // and because the task creation + rec acceptance happen in a
        // single DB transaction there is no half-state to recover.
        await acceptRecommendationAsTask(s.id, {
          title,
          description,
          priority: 2,
        });
      } else if (s.action === "open_goal" && s.goal_id) {
        // No DB write — just record acceptance + let the confirmation
        // banner's "View {goal} →" link be the real call to action.
        await authFetch(`/api/recommendations/${s.id}/mark-accepted`, {
          method: "POST",
        }).catch(() => undefined);
      } else if (s.action === "save_goal") {
        await authFetch(`/api/recommendations/${s.id}/accept`, {
          method: "POST",
        });
      } else {
        await authFetch(`/api/recommendations/${s.id}/mark-accepted`, {
          method: "POST",
        });
      }
      // ADR-0005: an accepted recommendation may create a goal/task/block —
      // tell every surface to refetch so it appears without a tab remount.
      notifyGoalChange();
      notifyTaskChange();
      notifyScheduleChange();
      void refresh();
    } catch (err) {
      // Re-throw so the AI card stays in its default state and the
      // user sees they can retry, instead of a false "✓ Added" banner.
      throw err;
    }
  };

  const handleSkip = async (
    s: ReflectAiSuggestionItem,
    reason: string,
  ): Promise<void> => {
    try {
      await authFetch(`/api/recommendations/${s.id}/dismiss`, {
        method: "POST",
        body: JSON.stringify({ reason }),
        headers: { "Content-Type": "application/json" },
      });
    } catch {
      // Best-effort; UI advances regardless. Next projection refresh resolves.
    }
    setRecommendation(null);
  };

  // Prime the goal-focus store before navigating so GoalsScreen scrolls
  // the right card into view + decorates it as `goal-focused-card`.
  // `onNavigate("goals")` alone just renders the list at the top.
  const focusGoalAndNavigate = (goalId: string, goalTitle: string) => {
    setGoalFocusTarget(
      buildGoalFocusTarget({ goalId, goalTitle, source: "growth" }),
    );
    onNavigate("goals");
  };

  const handleViewGoal = (goalId: string) => {
    const match = goals.find((g) => g.id === goalId);
    focusGoalAndNavigate(goalId, match?.title ?? "this goal");
  };

  const handleGoalClick = (goalId: string, goalTitle: string) => {
    if (goalId.startsWith("pulse-")) return; // synthetic; nothing to navigate to
    focusGoalAndNavigate(goalId, goalTitle);
  };

  // Overlay each projection area's `target_minutes_per_week` with the
  // raw value from `getLifeAreas()` so the committed minutes match
  // Balance exactly. Areas present only in projection (rare) keep their
  // projection target. Hoisted above the early-return guards because
  // hooks must run on every render.
  const allocationAreas = useMemo(() => {
    const projAreas = projection?.allocation?.areas ?? [];
    if (lifeAreas.length === 0) return projAreas;
    const lifeByKey = new Map(lifeAreas.map((la) => [la.area, la]));
    return projAreas.map((pa) => {
      const la = lifeByKey.get(pa.area);
      if (!la) return pa;
      return {
        ...pa,
        target_minutes_per_week: la.target_minutes_per_week ?? 0,
      };
    });
  }, [projection?.allocation?.areas, lifeAreas]);

  if (status === "loading" && !projection) {
    return (
      <div className="px-4 pt-4 pb-20">
        <ReflectLoading />
      </div>
    );
  }

  if (status === "error" && !projection) {
    return (
      <div className="px-4 pt-4 pb-20">
        <div className="rounded-3xl border border-amber/25 bg-amber/10 px-5 py-5 shadow-sm">
          <p className="text-sm font-semibold text-earth">
            Couldn&apos;t load Reflect yet.
          </p>
          <p className="mt-2 text-sm leading-relaxed text-text-light">
            The projection failed, so we&apos;re showing an honest retry state
            instead of synthetic content.
          </p>
          <button
            type="button"
            onClick={() => void refresh()}
            className="mt-4 rounded-full bg-forest px-4 py-2 text-sm font-semibold text-white transition hover:bg-forest-med"
          >
            Retry
          </button>
          {error ? <p className="mt-2 text-xs text-muted">{error}</p> : null}
        </div>
      </div>
    );
  }

  const driftGoals = driftSummary?.top_drifting_goals ?? [];

  return (
    <div
      className={`${styles["rf-shell"]} px-4 pt-4 pb-20 max-w-[920px] mx-auto`}
    >
      <header className="mb-5">
        <p className={styles["rf-eyebrow"]}>Reflect</p>
        <h1 className={styles["rf-h1"]}>This week, in honest light</h1>
        <p className={styles["rf-lede"]}>
          Where your time actually went, what&apos;s drifting, and the one
          adjustment most worth trying next.
        </p>
      </header>

      <ReflectBalanceHero
        areas={allocationAreas}
        goals={goals}
        onAdjustClick={() => onNavigate("balance")}
      />

      <section className={styles["rf-section"]}>
        <h2 className={styles["rf-section-h"]}>What&apos;s worth considering</h2>
        <ReflectAiSuggestion
          suggestions={aiSuggestions}
          loading={recommendationLoading}
          onTry={handleTry}
          onSkip={handleSkip}
          onViewGoal={handleViewGoal}
          onViewToday={() => {
            // If the user just accepted a schedule_block whose start was
            // today, push the new block's id into chatRevealStore so
            // TodayScreen's existing useChatRevealHighlight pipeline can
            // scroll + pulse the row. Without this, the navigation lands
            // on Today but the user has to find the new block themselves.
            if (
              lastScheduledReveal &&
              lastScheduledReveal.kind === "today"
            ) {
              requestChatReveal({
                surface: "today",
                id: lastScheduledReveal.id,
                label: "View in Today",
              });
            }
            onNavigate("present");
          }}
          onViewPlan={() => {
            // Plan reveal needs the target DATE in addition to the id —
            // Plan's date picker shows TODAY by default, and the block
            // lives on a future day (whichever resolveScheduleStart
            // computed). Without `date`, ScheduleGoalsScreen would
            // render today's blocks, `data-reveal-id` query would miss,
            // pulse would no-op (dogfood 2026-05-28 ship-blocker P0-1).
            // The destination subscribes to chatRevealStore and switches
            // selectedDate when active.date is set.
            if (
              lastScheduledReveal &&
              lastScheduledReveal.kind === "plan"
            ) {
              requestChatReveal({
                surface: "plan",
                id: lastScheduledReveal.id,
                label: "View in Plan",
                date: lastScheduledReveal.date ?? undefined,
              });
            }
            onNavigate("schedule");
          }}
        />
      </section>

      <section className={styles["rf-section"]}>
        <MemorySection memory={projection?.memory ?? []} />
      </section>

      <section className={styles["rf-section"]}>
        <h2 className={styles["rf-section-h"]}>How are the rhythms?</h2>
        <ReflectRhythmsGrid
          driftGoals={driftGoals}
          steadyGoals={goals}
          pulse={projection?.pulse ?? null}
          onGoalClick={handleGoalClick}
        />
      </section>

      <section className={styles["rf-section"]}>
        <h2 className={styles["rf-section-h"]}>Still echoing</h2>
        <ReflectStillEchoing chips={echoChips} />
      </section>
    </div>
  );
}
